<template>
  <div class="external-login-form-container">
    <form
      id="modules-vendors-login-form"
      class="external-login-form"
      data-abide
      novalidate
      @submit.prevent
    >
      <div class="welcome-panel">
        <a @click="navToHKLandLogin">{{ $t('loginForm.welcome') }}</a>
      </div>
      <div class="region-switch">
        <label>{{ $t('loginForm.chooseRegionNote') }}</label>
        <PublicSwitchRegion />
      </div>
      <div class="row">
        <label class="small-12 columns">{{ $t('chooseLogin') }}</label>
        <HKLSelect
          v-model="selectedDomainValue"
          class="small-6 columns"
          :options="loginPageOptions"
        />
      </div>

      <div class="info-input">
        <label>
          {{ $t('username') }}
          <input
            id="modules-vendors-login-form-input-username"
            v-model="username"
            type="email"
            name="username"
            class="text-input"
            placeholder="username@example.com"
            pattern="email"
            required
            @input="validate"
          >
          <span class="form-error">{{ $t('loginForm.usernameTip') }}</span>
        </label>
      </div>

      <div class="info-input">
        <label for="modules-vendors-login-form-input-password">
          {{ $t('password') }}
        </label>
        <div class="password-input-container">
          <input
            id="modules-vendors-login-form-input-password"
            v-model="password"
            :type="isPasswordVisible ? 'text' : 'password'"
            name="password"
            class="text-input password-input"
            :placeholder="$t('password')"
            pattern=".{6,}"
            required
            @input="validate"
          >
          <button
            type="button"
            class="password-visibility-toggle"
            :aria-label="isPasswordVisible ? 'Hide password' : 'Show password'"
            :aria-pressed="isPasswordVisible.toString()"
            :title="isPasswordVisible ? 'Hide password' : 'Show password'"
            @click="togglePasswordVisibility"
          >
            <svg
              v-if="isPasswordVisible"
              class="password-visibility-icon"
              aria-hidden="true"
              width="40"
              height="38"
              viewBox="0 0 40 38"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 19.4179C10 19.4179 13.5496 13.4151 20 13.4151C26.4504 13.4151 30 19.4179 30 19.4179"
                stroke="#0B3A59"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
              <path
                d="M23.5837 19.4179C23.5837 21.3899 21.9793 22.9884 20 22.9884"
                stroke="#0B3A59"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
              <path
                d="M17.368 21.8435C16.776 21.2066 16.4141 20.3543 16.4141 19.4179C16.4141 17.4459 18.0185 15.8473 19.9977 15.8473C20.87 15.8473 21.6692 16.1578 22.2907 16.6739"
                stroke="#0B3A59"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
              <path
                d="M27.5473 12.3351L13.2812 25.6649"
                stroke="#0B3A59"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
            </svg>
            <svg
              v-else
              class="password-visibility-icon"
              aria-hidden="true"
              width="40"
              height="38"
              viewBox="0 0 40 38"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 19.4768C10 19.4768 13.5496 13.4737 20 13.4737C26.4504 13.4737 30 19.4765 30 19.4765"
                stroke="#0B3A59"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
              <path
                d="M22.5357 22.0015C23.9352 20.6071 23.9352 18.3464 22.5357 16.952C21.1362 15.5576 18.8672 15.5576 17.4676 16.952C16.0681 18.3464 16.0681 20.6071 17.4676 22.0015C18.8672 23.3959 21.1362 23.3959 22.5357 22.0015Z"
                stroke="#0B3A59"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      <div
        v-show="selectedDomainValue"
        class="reset-link"
      >
        <div class="rest-link-item">
          <a
            @click="navToForgotPwd()"
          >
            {{ $t('forgotPassword') }}
          </a>
        </div>
        <div class="rest-link-item">
          <a
            @click="navToUnlockAct()"
          >
            {{ $t('unlockAccount') }}
          </a>
        </div>
      </div>
      <div class="login-btn-row">
        <fieldset
          v-if="force2FA"
          class="login-btn"
        >
          <button
            id="modules-vendors-login-form-button-submit"
            class="btn btn--orange radius round"
            type="submit"
            :disabled="disabled"
            @click="login()"
          >
            {{ $t('next') }}
          </button>
        </fieldset>
        <fieldset
          v-if="!force2FA"
          class="login-btn"
        >
          <button
            id="mock-modules-vendors-login-form-button-submit"
            class="btn btn--orange radius round"
            type="submit"
            :disabled="force2FA || disabled"
            @click="mockLogin()"
          >
            {{ $t('login') }}
          </button>
        </fieldset>
      </div>
      <div
        v-show="selectedDomainValue"
        class="term-of-use"
      >
        <div>
          <input
            id="login-terms"
            v-model="readChecked"
            type="checkbox"
            @change="() => validate()"
          >
        </div>
        <div>
          <label
            for="login-terms"
            v-html="$t(`iaccept.${publicPathLastSelectedRegion.name}`, [termHref])"
          />
        </div>
      </div>
      <div
        v-show="shouldShowPrivacyPolicy"
        class="private-policy term-of-use"
      >
        <div>
          <input
            id="login-pp"
            v-model="readPrivacyPolicyChecked"
            type="checkbox"
            @change="() => validate()"
          >
        </div>
        <div>
          <label
            for="login-pp"
            v-html="$t(`iAcceptPP.${publicPathLastSelectedRegion.name}`, [privacyPolicyLink])"
          />
        </div>
      </div>
    </form>
    <HKLModal
      ref="confirmMailModal"
      :hasCancelBtn="false"
      :closable="false"
      :title="$t('settings.password.expireHint')"
    />
    <HKLModal
      ref="accountBlockModal"
      :hasCancelBtn="false"
      :closable="false"
    >
      <template
        slot="title"
      >
        <div v-html="$t('accountLocked')" />
      </template>
    </HKLModal>
    <HKLModal
      ref="pwdExpiredModal"
      :hasCancelBtn="false"
      :closable="true"
      :title="$t('hint.expired.title')"
      okBtnAction="confirm"
      @onOkClick="confirm"
    >
      <HKLRadioButton
        v-model="isExtend"
        class="extend-option"
        :value="false"
      >
        {{ $t('hint.expired.changeNow') }}
      </HKLRadioButton>
      <HKLRadioButton
        v-model="isExtend"
        class="extend-option"
        :value="true"
      >
        {{ $t('hint.expired.keepCurrent') }}
      </HKLRadioButton>
    </HKLModal>
  </div>
</template>

<script>
import Status from 'http-status-codes'
import lodash from 'lodash'
import { computed, ref, unref, watch, getCurrentInstance } from 'vue-demi'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { loginAuth, loginMock, resetCookies, resetExpiredPassword } from '@/api/auth/request'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import filters from '@/filters/filters'
import usePublicLogin from '@/pages/usePublicLogin'
import router from '@/router/router'
import { useStoreGetters } from '@/store'
import types from '@/store/mutation-types'
import { resetStore } from '@/store/util'
import { PUBLIC_PORTAL_FEATURE_MAPPING, UserDomain, UserType } from '@/utils/consts'
import i18n from '@/utils/i18n'
import navigation from '@/utils/navigation'
import notification from '@/utils/notification'
import { isConvertiblePath } from '@/utils/redirect'
import { getExtHomePageByUserDomain } from '@/pages/utils'
import HKLModal from '@/controls/Modal/HKLModal'
import { useRefModal } from '@/controls/Modal/modal.hook'
import HKLRadioButton from '@/controls/Button/HKLRadioButton'
import PublicSwitchRegion from './PublicSwitchRegion.vue'

export default {
  name: 'PublicLoginForm',
  components: {
    PublicSwitchRegion,
    HKLSelect,
    HKLModal,
    HKLRadioButton,
  },
  setup () {
    const { proxy } = getCurrentInstance()

    const isExtend = ref(false)

    const { toNextPage } = usePublicLogin()
    const { modal: accountBlockModal, open: openAccountBlockModal } = useRefModal()
    const { modal: pwdExpiredModal, open: openPwdExpiredModal } = useRefModal()
    const { modal: confirmMailModal, open: openConfirmMailModal } = useRefModal()

    const { publicPathLastSelectedRegion } = useStoreGetters('regions', ['publicPathLastSelectedRegion'])
    const selectedDomainValue = ref('')
    const readChecked = ref(false)
    const readPrivacyPolicyChecked = ref(true)
    const disabled = ref(true)
    const isVendorPortalEnabled = computed(() => {
      const featureNames = unref(publicPathLastSelectedRegion).features.map(feature => feature.name)
      return PUBLIC_PORTAL_FEATURE_MAPPING.VENDOR_PORTAL.some(item => featureNames.includes(item))
    })
    const isTenantPortalEnabled = computed(() => {
      const featureNames = unref(publicPathLastSelectedRegion).features.map(feature => feature.name)
      return PUBLIC_PORTAL_FEATURE_MAPPING.TENANT_PORTAL.some(item => featureNames.includes(item))
    })
    const loginPageOptions = computed(() => {
      const region = unref(publicPathLastSelectedRegion).name
      const vendorI18nPath = filters.chooseRegion('loginForm.domainOption.vendor', region)
      const tenantI18nPath = filters.chooseRegion('loginForm.domainOption.tenant', region)
      const tenantContractorI18nPath = filters.chooseRegion('loginForm.domainOption.tenantContractor', region)
      const cmsOptions = [{
        name: i18n.t(vendorI18nPath),
        value: 1,
        rawValue: UserDomain.CMS,
      }]
      const tmsOptions = [{
        name: i18n.t(tenantI18nPath),
        value: 2,
        rawValue: UserDomain.TMS,
      }, {
        name: i18n.t(tenantContractorI18nPath),
        value: 3,
        rawValue: UserDomain.TMS,
      }]
      const result = []
      if (unref(isVendorPortalEnabled)) {
        result.push(...cmsOptions)
      }
      if (unref(isTenantPortalEnabled)) {
        result.push(...tmsOptions)
      }
      return result
    })
    const selectedDomain = computed(() => {
      const result = unref(loginPageOptions).find(item => item.value === unref(selectedDomainValue))
      return lodash.get(result, 'rawValue')
    })
    const termHref = computed(() => {
      const name = unref(selectedDomain) === UserDomain.CMS ? 'contractorTermsOfUse' : 'tenantTermsOfUse'
      const routerLink = router.resolve({
        name,
      })
      return routerLink.href
    })
    function navToForgotPwd () {
      router.push({ name: 'forgotPwd', query: { domain: unref(selectedDomain) } })
    }
    function navToUnlockAct () {
      router.push({ name: 'unlockAccount', query: { domain: unref(selectedDomain) } })
    }
    async function confirm () {
      if (unref(isExtend)) {
        proxy.force2FA ? await loginAfterExtend() : await mockLoginAfterExtend()
      } else {
        await resetPwd()
      }
    }
    async function resetPwd () {
      openConfirmMailModal()
      await resetExpiredPassword(proxy.username, proxy.selectedDomain, proxy.currentRegion)
    }
    function actionAfterLogin (response) {
      const contacts = lodash.get(response, ['data', 'data'])
      const params = {
        ...contacts,
        activePortal: proxy.selectedDomain,
      }
      proxy.toAuthenticationPage(params)
    }
    async function actionAfterMockLogin (response) {
      proxy.setJwt(response.headers.authorization)

      const user = lodash.get(response, ['data', 'data', 'user'])
      const homePage = getExtHomePageByUserDomain(proxy.selectedDomain)

      await proxy.postLoginAction({ user })
      await proxy.loadContact({ accountId: user.accountId, userId: user.id, externalId: user.externalId })
      await proxy.loadPerms({ perms: proxy.enhancePerms(response.data.data.acl, user) })
      proxy.toNextPage(homePage, (path) => {
        path.startsWith('/vendors') || path.startsWith('/tenants')
      })
    }
    async function loginAfterExtend () {
      try {
        const response = await loginAuth({username: proxy.username, password: proxy.password, extend: true}, proxy.selectedDomain)
        actionAfterLogin(response)
      } catch (error) {
        await proxy.handleLoginError(error)
      }
    }
    async function mockLoginAfterExtend () {
      if (!proxy.force2FA) {
        try {
          const response = await loginMock({username: proxy.username, password: proxy.password, extend: true}, proxy.selectedDomain)
          await actionAfterMockLogin(response)
        } catch (error) {
          await proxy.handleLoginError(error)
        }
      }
    }
    watch(publicPathLastSelectedRegion, (value, preVal) => {
      const valName = lodash.get(value, 'name')
      const preValName = lodash.get(preVal, 'name')
      if (valName !== preValName || !valName || !preValName) {
        selectedDomainValue.value = ''
        readChecked.value = false
        readPrivacyPolicyChecked.value = !unref(shouldShowPrivacyPolicy)
        disabled.value = true
      }
    })

    watch(selectedDomainValue, (value, preVal) => {
      if (value !== preVal || !value || !preVal) {
        readChecked.value = false
        readPrivacyPolicyChecked.value = !unref(shouldShowPrivacyPolicy)
        disabled.value = true
      }
    })
    const shouldShowPrivacyPolicy = computed(() => {
      const region = unref(publicPathLastSelectedRegion).name
      const hasAcceptance = i18n.te(`iAcceptPP.${region}`) && !lodash.isEmpty(i18n.t(`iAcceptPP.${region}`))
      const isTms = unref(selectedDomain) === UserDomain.TMS
      return isTms && hasAcceptance
    })
    const privacyPolicyLink = computed(() => {
      const routerLink = router.resolve({
        name: 'tenantPrivacyPolicy',
      })
      return routerLink.href
    })
    return {
      shouldShowPrivacyPolicy,
      readPrivacyPolicyChecked,
      privacyPolicyLink,
      isVendorPortalEnabled,
      isTenantPortalEnabled,
      termHref,
      loginPageOptions,
      selectedDomainValue,
      selectedDomain,
      readChecked,
      disabled,
      toNextPage,
      navToForgotPwd,
      navToUnlockAct,
      openAccountBlockModal,
      accountBlockModal,
      pwdExpiredModal,
      openPwdExpiredModal,
      confirmMailModal,
      openConfirmMailModal,
      actionAfterLogin,
      actionAfterMockLogin,
      loginAfterExtend,
      mockLoginAfterExtend,
      resetPwd,
      isExtend,
      confirm,
    }
  },
  data () {
    return {
      username: '',
      password: '',
      isPasswordVisible: false,
    }
  },
  computed: {
    ...mapGetters('user', ['currentUser']),
    ...mapGetters('settings', ['isAdMock', 'force2FA']),
    ...mapGetters('regions', ['publicPathLastSelectedRegion', 'currentRegion']),
  },
  async mounted () {
    this.form = $('[id^=modules-vendors-login-form]')
    this.abide = new Foundation.Abide(this.form, {
      liveValidate: true,
    })
    $('body').addClass('terms-of-use-print')
  },
  destroyed () {
    this.abide.destroy()
    $('body').removeClass('terms-of-use-print')
  },
  methods: {
    validate () {
      this.username = (this.username || '').trim()
      this.abide.validateForm()
      const isInputInvalid = $('input.is-invalid-input').length !== 0
      this.disabled = !this.selectedDomain || isInputInvalid || !this.readChecked || !this.readPrivacyPolicyChecked
    },
    togglePasswordVisibility () {
      this.isPasswordVisible = !this.isPasswordVisible
    },
    async navToHKLandLogin () {
      const originalParams = this.$router.currentRoute.params
      const pageName = this.isAdMock ? 'login' : 'adLogin'
      let pageParams = {}
      if (originalParams.redirect && isConvertiblePath(originalParams.redirect)) {
        pageParams = originalParams
      }
      navigation.toPage(pageName, pageParams)
    },
    findSubVendorRole (user) {
      return lodash.get(user, 'roles', []).find(role => role.usertype === UserType.Vendor.User)
    },
    enhancePerms (acl, user) {
      const subVendor = this.findSubVendorRole(user)
      if (subVendor) {
        acl[subVendor.id] = user.perms
      }
      return acl
    },
    toAuthenticationPage (params) {
      this.$router.push({ name: 'authenticationCode', params })
    },
    async resetBeforeLoginAuth () {
      await resetCookies()
      resetStore()
      await this.initRegionData()
    },
    async handleLoginError (error) {
      if (error.response && error.response.status === Status.GONE) {
        this.openPwdExpiredModal()
      } else if (error.response && error.response.status === Status.LOCKED) {
        this.openAccountBlockModal()
      } else {
        notification.handleResponseError(error, 'login')
      }
    },
    async login () {
      try {
        await this.resetBeforeLoginAuth()
        this.updateCurrentRegion(this.publicPathLastSelectedRegion.name)
        const response = await loginAuth({username: this.username, password: this.password}, this.selectedDomain)
        this.actionAfterLogin(response)
      } catch (error) {
        await this.handleLoginError(error)
      }
    },
    async mockLogin () {
      if (!this.force2FA) {
        try {
          await this.resetBeforeLoginAuth()
          this.updateCurrentRegion(this.publicPathLastSelectedRegion.name)
          const response = await loginMock({username: this.username, password: this.password}, this.selectedDomain)
          await this.actionAfterMockLogin(response)
        } catch (error) {
          await this.handleLoginError(error)
        }
      }
    },
    ...mapActions('regions', ['initRegionData', 'updateCurrentRegion', 'updatePublicPathLastSelectedRegion']),
    ...mapActions('access', {
      loadPerms: 'loadPerms',
    }),
    ...mapActions('user', {
      loadContact: 'loadContact',
    }),
    ...mapActions('user', [
      'postLoginAction',
    ]),
    ...mapMutations('settings', {
      setJwt: types.JSON_WEB_TOKEN,
    }),
  },
}
</script>

<style lang='scss' scoped>
@import '~styles/settings';
@import '~styles/login/loginForm';

.external-login-form-container {
  height: calc(100vh - 126px);
  overflow: auto;
  padding: 10px 0;
  display: flex;
  justify-content: center;
  align-items: safe center;
  .external-login-form {
    width: 660px;
    border: 1px solid $medium-gray;
    padding: $form-spacing;
  }
}
.extend-option {
  margin-bottom: 10px;
  font-weight: bold;
}
.reset-link {
  margin: 20px 0 0;
}
.modal-container {
  margin-left: 20px;
  padding-top: 20px;
  span {
    margin: 20px auto;
    font-family: Georgia;
    font-size: 20px;
  }
}

.modal-btn-bar {
  margin-top: 20px;
  text-align: center;
}

.term-of-use {
  display: flex;
  margin-top: 25px;
  text-align: justify;
  input[type="checkbox"] {
    margin-top: 1px;
    margin-right: 14px;
  }
  label {
    font-size: 12px;
    font-weight: normal;
    margin: 0;
    ::v-deep a {
      word-break: break-all;
    }
  }
}
.region-switch {
  width: 50%;
  min-width: 200px;
}
</style>
